import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsTags } from 'react-icons/bs';

import { useWzCodesTree } from '../../../models/wzCodes/queries';

import { WzCodePickerModal } from './WzCodePickerModal';

/**
 * Section that exposes the WZ-2025 code picker via a button.
 * Used in both the sidebar and the extend-search modal.
 *
 * Calls useWzCodesTree() eagerly so the code-split chunk starts downloading
 * as soon as the section is mounted — even before the user clicks the button.
 * This avoids a first-render race where the modal opens before the data arrives.
 */
export const WzCodePickerSection = ({ filters, onFilterChange, disabled }) => {
	const [isWzPickerOpen, setIsWzPickerOpen] = useState(false);

	// Prefetch the WZ tree chunk as soon as this section mounts.
	// React Query dedupes by queryKey, so the modal will reuse the same cache entry.
	useWzCodesTree();

	const selectedWzCodes = Array.isArray(filters.industryCodes) ? filters.industryCodes : [];

	const handleConfirm = (codes) => {
		onFilterChange('industryCodes', codes);
		setIsWzPickerOpen(false);
	};

	return (
		<div className="px-4 py-3 border-t border-gray-200">
			<button
				type="button"
				onClick={() => setIsWzPickerOpen(true)}
				disabled={disabled}
				className={`w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs border rounded-md transition-colors ${
					disabled
						? 'border-gray-200 text-bgBase-800 bg-gray-50 cursor-not-allowed'
						: selectedWzCodes.length > 0
							? 'border-purple-300 text-primary-700 bg-purple-50 hover:bg-purple-100'
							: 'border-gray-300 text-text hover:border-purple-400 hover:bg-purple-50'
				}`}
				title={
					disabled
						? 'WZ-Codes sind nur bei der Handelsregister-Suche verfügbar'
						: 'WZ-2025 Branchencodes manuell auswählen'
				}
			>
				<BsTags className="w-3.5 h-3.5" />
				{selectedWzCodes.length > 0
					? `WZ-Codes bearbeiten (${selectedWzCodes.length})`
					: 'WZ-Codes manuell definieren'}
			</button>

			<WzCodePickerModal
				isOpen={isWzPickerOpen}
				onClose={() => setIsWzPickerOpen(false)}
				onConfirm={handleConfirm}
				initialSelection={selectedWzCodes}
			/>
		</div>
	);
};

WzCodePickerSection.propTypes = {
	filters: PropTypes.object.isRequired,
	onFilterChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};

export default WzCodePickerSection;
